import { Fragment, useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import Badge from "../ui/badges/Badge";
import CarouselArrows from "../ui/CarouselArrows";
import GhostBar from "../ui/loading/skeletons/GhostBar";

/**
 * SegmentMenu - Navigation component that renders horizontal tab segments or buttons.
 *
 * @param {Object[]} segments - Array of segment configuration objects.
 * @param {string} [segments[].path] - Router path / URL query string for the segment tab.
 * @param {string} [segments[].text] - Label text displayed inside the segment tab.
 * @param {string} [segments[].normalIcon] - URL/Path to default inactive icon image.
 * @param {string} [segments[].activeIcon] - URL/Path to active tab icon image.
 * @param {string} [segments[].iconWidth] - Optional custom CSS width for the icon (e.g. "20px").
 * @param {string} [segments[].iconHeight] - Optional custom CSS height for the icon (e.g. "20px").
 * @param {boolean} [segments[].disabled] - Whether the segment is disabled.
 * @param {number} [segments[].stepNumber] - Step number if rendering numbered steps instead of icons.
 * @param {string} [segments[].badge] - Optional badge text (e.g. "NEW").
 * @param {Function} [segments[].onClick] - Optional click handler for static buttons.
 * @param {string} currentPath - Active path string matching segment.path.
 * @param {boolean} [hideForMobile=true] - Whether to hide/style for mobile displays.
 * @param {boolean} [isStatic=false] - If true, renders <button> instead of <Link>.
 * @param {string} [className=""] - Extra CSS container class.
 * @param {string} [title=""] - Optional section title.
 * @param {React.ReactNode} [cta=null] - Optional call-to-action node.
 * @param {React.ReactNode} [headerLeftContent=null] - Optional left-aligned header content.
 * @param {boolean} [loading=false] - Displays ghost skeleton state if true.
 * @param {number} [ctaGhostCount=0] - Number of skeleton CTA bars to render while loading.
 */
function SegmentMenu({
  segments,
  currentPath,
  hideForMobile = true,
  isStatic = false,
  className = "",
  title = "",
  cta = null,
  headerLeftContent = null,
  loading = false,
  ctaGhostCount = 0,
}) {
  const [scrollPosition, setScrollPosition] = useState(0);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const containerRef = useRef(null);

  const updateArrowVisibility = () => {
    if (containerRef.current) {
      const container = containerRef.current;
      const scrollWidth = container.scrollWidth;
      const clientWidth = container.clientWidth;
      const isScrollable = scrollWidth > clientWidth;

      setShowLeftArrow(isScrollable && scrollPosition > 0);
      setShowRightArrow(
        isScrollable && scrollPosition < scrollWidth - clientWidth,
      );
    }
  };

  const scrollLeft = () => {
    if (containerRef.current) {
      const scrollAmount = 200;
      const newPosition = Math.max(0, scrollPosition - scrollAmount);
      containerRef.current.scrollTo({ left: newPosition, behavior: "smooth" });
      setScrollPosition(newPosition);
    }
  };

  const scrollRight = () => {
    if (containerRef.current) {
      const scrollAmount = 200;
      const maxScroll =
        containerRef.current.scrollWidth - containerRef.current.clientWidth;
      const newPosition = Math.min(maxScroll, scrollPosition + scrollAmount);
      containerRef.current.scrollTo({ left: newPosition, behavior: "smooth" });
      setScrollPosition(newPosition);
    }
  };

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      const handleScroll = () => setScrollPosition(container.scrollLeft);
      container.addEventListener("scroll", handleScroll);
      updateArrowVisibility();
      return () => container.removeEventListener("scroll", handleScroll);
    }
  }, [scrollPosition]);

  useEffect(() => {
    const timer = setTimeout(() => updateArrowVisibility(), 100);
    return () => clearTimeout(timer);
  }, [segments, loading]);

  return (
    <Fragment>
      {(title || cta) && (
        <div className="segment-menu-header">
          <div style={{ flex: 1, width: "100%" }}>
            {title &&
              (loading ? (
                <GhostBar
                  width="150px"
                  height="24px"
                  style={{ marginBottom: "10px" }}
                />
              ) : (
                <h2 style={{ position: "absolute", left: 0 }}>{title}</h2>
              ))}
            {cta && (
              <div className="segment-menu-cta">
                {loading ? (
                  <Fragment>
                    {Array.from({ length: ctaGhostCount }).map((_, i) => (
                      <GhostBar
                        key={i}
                        width="110px"
                        height="40px"
                        style={{ borderRadius: "20px" }}
                      />
                    ))}
                  </Fragment>
                ) : (
                  cta
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <div
        className={`segment-menu ${className} ${
          hideForMobile ? "segment-menu-hide" : ""
        }`}
      >
        {!loading && (showLeftArrow || showRightArrow || headerLeftContent) && (
          <div className="segment-menu-header">
            {headerLeftContent && (
              <div className="segment-header-left">{headerLeftContent}</div>
            )}
            {(showLeftArrow || showRightArrow) && (
              <CarouselArrows
                onLeftClick={scrollLeft}
                onRightClick={scrollRight}
                isLeftDisabled={!showLeftArrow}
                isRightDisabled={!showRightArrow}
                size="small"
                variant="outline"
                className="segment-scroll-arrows"
              />
            )}
          </div>
        )}

        <div className="segment-container">
          <div className="segment-buttons" ref={containerRef}>
            {loading ? (
              <Fragment key="skeleton-segments">
                {segments.map((_, index) => (
                  <div
                    key={`ghost-${index}`}
                    className="segment-button"
                    style={{ border: "none", cursor: "default" }}
                  >
                    <GhostBar width="20px" height="20px" circle />
                    <GhostBar width="80px" height="14px" />
                  </div>
                ))}
              </Fragment>
            ) : (
              <Fragment key="real-segments">
                {segments.map((segment, index) => {
                  const isActive = currentPath === segment.path;
                  const isDisabled = segment.disabled || false;
                  const hasStepNumber = segment.stepNumber !== undefined;

                  const ComponentTag = isStatic ? "button" : Link;

                  const elementProps = isStatic
                    ? { type: "button", disabled: isDisabled }
                    : { to: isDisabled ? "#" : segment.path };

                  // Check if segment is "Spending" by label or path
                  const isSpending =
                    segment.text?.toLowerCase() === "spending" ||
                    segment.path?.toLowerCase().includes("spending");

                  // Build custom icon dimensions & rotation styles
                  const customIconStyle = {
                    ...(segment.iconWidth && { width: segment.iconWidth }),
                    ...(segment.iconHeight && { height: segment.iconHeight }),
                    ...(isSpending && { transform: "rotate(180deg)" }),
                  };

                  return (
                    <ComponentTag
                      key={index}
                      className={`segment-button ${isActive ? "active" : ""} ${
                        isDisabled ? "disabled" : ""
                      }`}
                      onClick={segment.onClick}
                      style={
                        isDisabled
                          ? { cursor: "not-allowed", opacity: 0.5 }
                          : { position: "relative" }
                      }
                      {...elementProps}
                    >
                      {hasStepNumber ? (
                        <div
                          className={`step-number-circle ${
                            isActive ? "active" : ""
                          }`}
                        >
                          {segment.stepNumber}
                        </div>
                      ) : (
                        segment.normalIcon && (
                          <img
                            src={
                              isActive ? segment.activeIcon : segment.normalIcon
                            }
                            alt={segment.text}
                            className="segment-icon"
                            style={
                              Object.keys(customIconStyle).length > 0
                                ? customIconStyle
                                : undefined
                            }
                          />
                        )
                      )}
                      <b>{segment.text}</b>
                      {segment.badge && (
                        <Badge
                          text={segment.badge}
                          variant="new"
                          size="small"
                          style={{
                            position: "absolute",
                            top: "1px",
                            right: "1px",
                          }}
                        />
                      )}
                    </ComponentTag>
                  );
                })}
              </Fragment>
            )}
          </div>
        </div>
      </div>
    </Fragment>
  );
}

export default SegmentMenu;
